import type { BillView } from "@vaulted/shared";
import { formatCents } from "@vaulted/shared";
import { useState } from "react";
import { Button, Field } from "./ui";

/**
 * The client's side of checkout (the iPad, or a pay link on their phone): each visit's bill, then a
 * tip for the nurse (the clinic's percentages, custom, or none) and the total with it.
 */
export function CheckoutBills({ bills, tipPercents, busyBillId, onTip }: { bills: Omit<BillView, "nurseId">[]; tipPercents: number[]; busyBillId: string | null; onTip: (billId: string, tipCents: number) => void }) {
  const [custom, setCustom] = useState<string | null>(null);
  const [customText, setCustomText] = useState("");
  return (
    <>
      {bills.map((b) => (
        <section key={b.id} className="rounded-[var(--radius-panel)] border border-line bg-white p-4">
          <h2 className="mb-2 font-semibold">{b.client.name.split(" ")[0]}'s visit</h2>
          <ul className="flex flex-col gap-1 text-sm">
            {b.lines.map((l) => (
              <li key={l.id} className="flex justify-between gap-3">
                <span>
                  {l.quantity > 1 && `${l.quantity} × `}
                  {l.name}
                  {l.coveredBy && <span className="text-muted"> · {l.coveredBy}</span>}
                </span>
                <span className="tabular-nums">{l.coveredBy ? formatCents(0) : formatCents(l.amountCents)}</span>
              </li>
            ))}
            {b.discountApplied && (
              <li className="flex justify-between gap-3 text-ink-soft">
                <span>{b.discountApplied === "plan" ? "Member pricing" : (b.discountReason ?? "Discount")}</span>
                <span className="tabular-nums">−{formatCents(b.discountApplied === "plan" ? b.planDiscountCents : b.discountCents)}</span>
              </li>
            )}
            {b.depositCreditCents > 0 && (
              <li className="flex justify-between gap-3 text-ink-soft">
                <span>Deposit paid</span>
                <span className="tabular-nums">−{formatCents(b.depositCreditCents)}</span>
              </li>
            )}
            {b.paidCents > 0 && (
              <li className="flex justify-between gap-3 text-ink-soft">
                <span>Already paid</span>
                <span className="tabular-nums">−{formatCents(b.paidCents)}</span>
              </li>
            )}
          </ul>
          <div className="mt-4">
            <p className="mb-2 text-sm font-semibold text-ink-soft">Add a tip for your nurse</p>
            <div className="flex flex-wrap gap-2">
              {tipPercents.map((pct) => {
                const cents = Math.round((b.tipBaseCents * pct) / 100);
                return (
                  <Button key={pct} variant={b.tipCents === cents && custom !== b.id ? "primary" : "secondary"} busy={busyBillId === b.id} onClick={() => (setCustom(null), onTip(b.id, cents))}>
                    {pct}% · {formatCents(cents)}
                  </Button>
                );
              })}
              <Button variant={custom === b.id ? "primary" : "secondary"} onClick={() => (setCustom(b.id), setCustomText(""))}>
                Custom
              </Button>
              <Button variant={b.tipCents === 0 && custom !== b.id ? "primary" : "secondary"} onClick={() => (setCustom(null), onTip(b.id, 0))}>
                No tip
              </Button>
            </div>
            {custom === b.id && (
              <form
                className="mt-2 flex items-end gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  const cents = Math.round(Number(customText.replace(/[^0-9.]/g, "")) * 100);
                  if (Number.isFinite(cents) && cents >= 0) onTip(b.id, cents);
                }}
              >
                <Field label="Tip ($)" inputMode="decimal" value={customText} onChange={(e) => setCustomText(e.target.value)} autoFocus />
                <Button type="submit">Add tip</Button>
              </form>
            )}
          </div>
          <div className="mt-4 flex justify-between border-t border-line pt-3 font-semibold">
            <span>Total{b.tipCents ? ` (with ${formatCents(b.tipCents)} tip)` : ""}</span>
            <span className="tabular-nums">{formatCents(b.balanceCents)}</span>
          </div>
        </section>
      ))}
    </>
  );
}
